<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useModalFocusTrap } from '../../composables/useModalFocusTrap';

/**
 * Il report non è partito perché uno o più prodotti selezionati portano una
 * quantità negativa.
 *
 * **Cosa sostituisce**: lo snackbar generico «ricarica la pagina». L'api
 * rifiutava già il peso sotto zero, ma con `error: 'validation_error'`, che
 * l'interceptor assorbe — chi aveva chiamato riceveva `null` e non aveva più
 * niente da dire. Qui il rifiuto arriva *prima* dell'invio, perché il client ha
 * già in mano le quantità: vedi `utils/negativeQuantityProducts`.
 *
 * **Perché un dialog e non uno snackbar**: stesso criterio del gemello
 * `DuplicateReportLinesModal`. Lo snackbar dura due secondi in basso a
 * sinistra, e questo messaggio nomina prodotti e va letto per poter agire. Può
 * portarne più di uno, perché la guardia li raccoglie tutti — fermarsi al primo
 * farebbe scoprire il secondo solo dopo aver corretto il primo, un tentativo di
 * invio per prodotto.
 *
 * La quantità mostrata è quella **calcolata dalla stessa aritmetica del
 * payload**, così la cifra che l'utente legge qui è la cifra che blocca il
 * report. Nomi interpolati come testo e non con `v-html`: li scrive l'utente.
 */

/* Emits */
const emits = defineEmits(['closeModal']);
/* Props */
const props = defineProps({
    /*
     * I prodotti già risolti da chi apre il dialog:
     * `{ name: string|null, weight: number }`.
     */
    products: { required: true, type: Array }
});
/* Data */
const { t } = useI18n();
const store = useStore();
const dialog = ref(null);
/* Computed */
/**
 * Un prodotto per riga: come si chiama, e quanto porterebbe.
 *
 * Un nome mancante non lascia un buco nella frase — `products.name` è nullable
 * e un prodotto senza nome resta comunque una riga del report.
 */
const rows = computed( () => props.products.map( (product) => ({
    name: product.name ?? t('negativeQuantityModal.unnamedProduct', store.state.locale),
    weight: product.weight
})));
/* Methods */
const close = () => emits('closeModal');
/* Hooks */
useModalFocusTrap(dialog, close);
</script>
<template>
    <div
        @click.prevent="close"
        class="fixed inset-0 p-6 bg-gray-1000 bg-opacity-90 z-40 overscroll-y-contain overflow-y-auto text-blue-500">
        <!-- `min-h-full` e non `h-full`: con un'altezza fissa, un dialog più
             alto del viewport viene centrato traboccando **da entrambi i
             lati**, e la parte sopra il bordo superiore non si raggiunge —
             `scrollTop` non scende sotto zero. Titolo e prime righe restavano
             fuori schermo senza modo di tornarci. Con `min-h-full` il
             contenitore cresce col contenuto, il centraggio diventa inerte e
             l'overlay scorre normalmente. -->
        <div class="flex flex-col w-full min-h-full items-center justify-center">
            <div
                @click.stop
                ref="dialog"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="negative-quantity-title"
                aria-describedby="negative-quantity-intro"
                class="bg-white dark:bg-gray-900 dark:text-gray-400 border dark:border-gray-700 rounded p-5 flex flex-col space-y-4 w-full md:w-2/3 lg:w-1/2 2xl:w-1/3">
                <div class="flex items-start justify-between space-x-3">
                    <h2 id="negative-quantity-title" class="text-xl flex items-center space-x-2 text-red-700 dark:text-red-300">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 fill-current flex-shrink-0" aria-hidden="true" focusable="false">
                            <path d="M12,2A10,10,0,1,0,22,12,10.011,10.011,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.009,8.009,0,0,1,12,20Zm.5-3.5a.75.75,0,1,1-.75-.75A.75.75,0,0,1,12.5,16.5ZM11.75,6.5a3,3,0,0,0-3,3,1,1,0,0,0,2,0,1,1,0,1,1,1,1,1,1,0,0,0-1,1v1.25a1,1,0,0,0,2,0v-.42a3,3,0,0,0-1-5.83Z"/>
                        </svg>
                        <span>{{ t('negativeQuantityModal.title', store.state.locale) }}</span>
                    </h2>
                    <button
                        :title="t('actions.close', store.state.locale)"
                        :aria-label="t('actions.close', store.state.locale)"
                        @click.prevent="close"
                        class="bg-transparent min-w-min flex items-center justify-center transform hover:rotate-90 duration-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" class="w-4 fill-current text-gray-800 dark:text-gray-400" aria-hidden="true" focusable="false">
                                <path d="M34.5,32L62.2,4.2c0.7-0.7,0.7-1.8,0-2.5c-0.7-0.7-1.8-0.7-2.5,0L32,29.5L4.2,1.8c-0.7-0.7-1.8-0.7-2.5,0
                                    c-0.7,0.7-0.7,1.8,0,2.5L29.5,32L1.8,59.8c-0.7,0.7-0.7,1.8,0,2.5c0.3,0.3,0.8,0.5,1.2,0.5s0.9-0.2,1.2-0.5L32,34.5l27.7,27.8
                                    c0.3,0.3,0.8,0.5,1.2,0.5c0.4,0,0.9-0.2,1.2-0.5c0.7-0.7,0.7-1.8,0-2.5L34.5,32z"/>
                            </svg>
                    </button>
                </div>

                <p id="negative-quantity-intro" class="text-sm">
                    {{ t('negativeQuantityModal.intro', store.state.locale) }}
                </p>

                <!-- Un prodotto per riga, con la quantità che lo blocca. Scorre
                     da sola se sono molti, senza allungare il dialog oltre lo
                     schermo.

                     `max-h-60` e non `max-h-64`: questa configurazione
                     ridefinisce `maxHeight` **fuori** da `extend`, quindi
                     sostituisce la scala di default, e `64` non c'è. La classe
                     non genera CSS — non è un valore sbagliato, è una regola
                     che non esiste — e senza tetto `overflow-y-auto` non ha
                     niente da limitare: la lista cresce e basta.

                     `tabindex="0"` perché una regione che scorre deve poter
                     scorrere anche da tastiera: qui dentro non c'è niente di
                     focalizzabile — solo testo — quindi senza questo il Tab
                     salta dalla lista al pulsante e le righe oltre il tetto
                     non si raggiungono. Serve anche al focus trap, che
                     enumera i focalizzabili per attributo e altrimenti non la
                     vedrebbe. Focalizzabile vuol dire annunciabile, da cui
                     l'etichetta. -->
                <ul
                    tabindex="0"
                    :aria-label="t('negativeQuantityModal.listLabel', store.state.locale)"
                    class="flex flex-col space-y-2 max-h-60 overflow-y-auto overscroll-contain rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-300">
                    <li
                        v-for="(row, index) in rows"
                        :key="index"
                        class="text-sm border rounded-md p-3 bg-gray-100 dark:bg-gray-800 dark:border-gray-700">
                        <span class="font-semibold">{{ row.name }}</span>
                        <!-- Il segno non è l'unico portatore dell'informazione:
                             la frase di sopra dice già che la quantità è sotto
                             zero, e qui la cifra è etichettata. -->
                        <span class="block pt-1">
                            {{ t('productTable.cols.quantity', store.state.locale) }}: {{ row.weight }} kg
                        </span>
                    </li>
                </ul>

                <div class="flex items-center justify-end">
                    <button
                        @click.prevent="close"
                        class="px-5 py-3 font-medium leading-5 text-center text-white bg-blue-500 hover:bg-blue-300 dark:hover:bg-blue-700 rounded duration-300 lg:w-auto focus:ring-2 focus:ring-blue-700 focus:ring-offset-2 dark:focus:ring-blue-300 dark:focus:ring-offset-gray-900">
                        {{ t('actions.close', store.state.locale) }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
